/**
 * Which graphics card the browser draws with, in a few words for the HUD.
 *
 * WebGL names it only through WEBGL_debug_renderer_info, which a browser may
 * withhold or blur (Firefox and Safari often give a generic family). Chrome
 * and Edge go through ANGLE and wrap the name in its backend's terms:
 *
 *   ANGLE (NVIDIA Corporation, NVIDIA GeForce RTX 4070 Laptop GPU/PCIe/SSE2, OpenGL 4.5.0 NVIDIA 615.78.08)
 *   ANGLE (AMD, AMD Radeon 890M Graphics (radeonsi strix1 ACO), OpenGL ES 3.2 Mesa 26.2.5)
 *   ANGLE (NVIDIA, Vulkan 1.4.351 (NVIDIA NVIDIA GeForce RTX 4070 Laptop GPU (0x00002820)), NVIDIA-615.78.8.0)
 *   ANGLE (NVIDIA, NVIDIA GeForce RTX 3060 Direct3D11 vs_5_0 ps_5_0, D3D11)
 *   ANGLE (Apple, ANGLE Metal Renderer: Apple M2, Unspecified Version)
 *
 * which `describeGpu` boils down to the card and the API under it.
 */

/** Split on the commas that are not inside parentheses. */
function topLevelFields(text: string): string[] {
  const fields: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ',' && depth === 0) {
      fields.push(text.slice(start, i).trim());
      start = i + 1;
    }
  }
  fields.push(text.slice(start).trim());
  return fields;
}

function backendOf(api: string, device: string): string | undefined {
  if (/^Vulkan/.test(device)) return 'Vulkan';
  if (/Metal/.test(device)) return 'Metal';
  if (/^OpenGL ES/.test(api)) return 'OpenGL ES';
  if (/^OpenGL/.test(api)) return 'OpenGL';
  if (/D3D11/.test(api)) return 'Direct3D 11';
  if (/D3D9/.test(api)) return 'Direct3D 9';
  return undefined;
}

/** The card and the API under it, from a WebGL renderer string. */
export function describeGpu(renderer: string): string {
  const angle = /^ANGLE \((.*)\)$/.exec(renderer.trim());
  if (angle === null) return renderer.trim();
  const [vendor = '', device = '', api = ''] = topLevelFields(angle[1]);
  let name = device;
  // Vulkan puts the card in parentheses after its own version, the vendor
  // often doubled: "Vulkan 1.4 (NVIDIA NVIDIA GeForce … (0x2820))".
  const vulkan = /^Vulkan [\d.]+ \((.*)\)$/.exec(name);
  if (vulkan !== null) name = vulkan[1];
  name = name
    .replace(/^ANGLE Metal Renderer: /, '')
    .replace(/ \(0x[0-9a-f]+\)$/i, '')
    .replace(/ Direct3D.*$/, '')
    .replace(/\/.*$/, '')
    .replace(/ \([^)]*\)$/, '');
  const doubled = `${vendor} ${vendor} `;
  if (vendor !== '' && name.startsWith(doubled)) name = name.slice(vendor.length + 1);
  const backend = backendOf(api, device);
  return backend === undefined ? name : `${name} · ${backend}`;
}

/** The graphics card `gl` draws with, or undefined where the browser does not say. */
export function readGpu(gl: WebGLRenderingContext | WebGL2RenderingContext): string | undefined {
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  const raw = info === null ? undefined : gl.getParameter(info.UNMASKED_RENDERER_WEBGL) as string | null;
  return raw === undefined || raw === null || raw === '' ? undefined : describeGpu(raw);
}
